import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';

export type MediaKind =
  | 'merchant_logo'
  | 'merchant_card'
  | 'merchant_cover'
  | 'product_photo'
  | 'marketing_image'
  | 'category_icon'
  | 'category_image';

interface MediaUploadFieldProps {
  kind: MediaKind;
  /** Do'kon yoki mahsulot/kategoriya ID */
  targetId?: string | null;
  /** Marketing elementi/ariza ID */
  refId?: string | null;
  value: string;
  onChange: (url: string) => void;
  /** Barqaror obyekt yo'li (marketing shakllari bazaga saqlaydi). */
  onPathChange?: (path: string) => void;
  label: string;
  hint?: string;
  /** Ko'rinish: kvadrat (logo/ikonka) yoki keng (karta/muqova/promo) */
  shape?: 'square' | 'wide';
  disabled?: boolean;
  disabledReason?: string;
}

/** Turlar bo'yicha maksimal hajm — server ham xuddi shu chegarani tekshiradi. */
const MAX_BYTES_BY_KIND: Record<MediaKind, number> = {
  merchant_logo: 5 * 1024 * 1024,
  merchant_card: 8 * 1024 * 1024,
  merchant_cover: 8 * 1024 * 1024,
  product_photo: 5 * 1024 * 1024,
  marketing_image: 8 * 1024 * 1024,
  category_icon: 5 * 1024 * 1024,
  category_image: 8 * 1024 * 1024,
};

const MERCHANT_URL_FIELD: Record<'merchant_logo' | 'merchant_card' | 'merchant_cover', string> = {
  merchant_logo: 'logo_url',
  merchant_card: 'card_image_url',
  merchant_cover: 'cover_image_url',
};

export function MediaUploadField({
  kind,
  targetId,
  refId,
  value,
  onChange,
  onPathChange,
  label,
  hint,
  shape = 'wide',
  disabled = false,
  disabledReason,
}: MediaUploadFieldProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const maxBytes = MAX_BYTES_BY_KIND[kind];
  const effectiveHint = hint ?? t('misc_media.hint', { mb: Math.round(maxBytes / 1024 / 1024) });

  const handleRemove = async () => {
    if (kind === 'product_photo') {
      if (!targetId) return;
      const { error } = await apiClient.DELETE('/products/{product}/photo', {
        params: { path: { product: Number(targetId) } },
      });
      if (error) {
        toast.error(t('misc_media.remove_error'));
        return;
      }
    } else if (kind === 'merchant_logo' || kind === 'merchant_card' || kind === 'merchant_cover') {
      const { error } = await apiClient.DELETE('/merchant/media/{kind}', { params: { path: { kind } } });
      if (error) {
        toast.error(t('misc_media.remove_error'));
        return;
      }
    } else if (kind === 'category_icon' || kind === 'category_image') {
      if (!targetId) return;
      const { error } = await apiClient.DELETE('/admin/global-categories/{globalCategory}/media/{kind}', {
        params: { path: { globalCategory: Number(targetId), kind } },
      });
      if (error) {
        toast.error(t('misc_media.remove_error'));
        return;
      }
    }
    // marketing_image: detached upload, no owning row to clear server-side —
    // the picked file only gets persisted once the parent form submits.
    onChange('');
    onPathChange?.('');
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > maxBytes) {
      toast.error(t('misc_media.too_big', { mb: Math.round(maxBytes / 1024 / 1024) }));
      return;
    }
    setUploading(true);
    try {
      if (kind === 'product_photo') {
        if (!targetId) throw new Error(t('misc_media.product_not_found'));
        const body = new FormData();
        body.append('file', file);
        const { data, error } = await apiClient.POST('/products/{product}/photo', {
          params: { path: { product: Number(targetId) } },
          body: body as unknown as never,
        });
        if (error) throw new Error((error as { message?: string })?.message || t('misc_media.upload_error'));
        const url = data?.data?.photo_url;
        if (!url) throw new Error(t('misc_media.no_url'));
        onChange(url);
      } else if (kind === 'merchant_logo' || kind === 'merchant_card' || kind === 'merchant_cover') {
        const body = new FormData();
        body.append('file', file);
        const { data, error } = await apiClient.POST('/merchant/media/{kind}', {
          params: { path: { kind } },
          body: body as unknown as never,
        });
        if (error) throw new Error((error as { message?: string })?.message || t('misc_media.upload_error'));
        const url = data?.data?.[MERCHANT_URL_FIELD[kind]];
        if (!url) throw new Error(t('misc_media.no_url'));
        onChange(url);
      } else if (kind === 'category_icon' || kind === 'category_image') {
        if (!targetId) throw new Error(t('misc_media.category_not_found'));
        const body = new FormData();
        body.append('file', file);
        const { data, error } = await apiClient.POST('/admin/global-categories/{globalCategory}/media/{kind}', {
          params: { path: { globalCategory: Number(targetId), kind } },
          body: body as unknown as never,
        });
        if (error) throw new Error((error as { message?: string })?.message || t('misc_media.upload_error'));
        const url = kind === 'category_icon' ? data?.data?.icon_url : data?.data?.image_url;
        if (!url) throw new Error(t('misc_media.no_url'));
        onChange(url);
      } else {
        const body = new FormData();
        body.append('file', file);
        const { data, error } = await apiClient.POST('/marketing/media', { body: body as unknown as never });
        if (error) throw new Error((error as { message?: string })?.message || t('misc_media.upload_error'));
        const url = data?.data?.url;
        if (!url) throw new Error(t('misc_media.no_url'));
        onChange(url);
        onPathChange?.(url);
      }
      toast.success(t('misc_media.uploaded'));
    } catch (err: any) {
      toast.error(err?.message || t('misc_media.upload_error'));
    } finally {
      setUploading(false);
    }
  };

  const boxClass = shape === 'square'
    ? 'w-20 h-20 rounded-xl'
    : 'h-24 w-full rounded-lg';

  return (
    <div>
      <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
      <div className={shape === 'square' ? 'flex items-center gap-4' : 'space-y-2'}>
        {value ? (
          <div className="relative">
            <img src={value} alt={label} className={`${boxClass} object-cover border border-border`} loading="lazy" />
            {!disabled && (
              <button
                type="button"
                onClick={handleRemove}
                className="absolute -top-2 -right-2 p-0.5 rounded-full bg-destructive text-destructive-foreground"
                aria-label={t('misc_media.remove_aria')}
              >
                <X size={12} />
              </button>
            )}
          </div>
        ) : (
          <div
            onClick={() => !disabled && inputRef.current?.click()}
            className={`${boxClass} border-2 border-dashed border-border flex items-center justify-center ${disabled ? 'opacity-50' : 'cursor-pointer hover:border-primary'} transition-colors`}
          >
            <ImagePlus size={20} className="text-muted-foreground" />
          </div>
        )}
        <div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || uploading}
          >
            {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
            {uploading ? t('misc_media.uploading') : t('misc_media.upload')}
          </Button>
          <p className="text-xs text-muted-foreground mt-1">{disabled ? (disabledReason || effectiveHint) : effectiveHint}</p>
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} />
    </div>
  );
}
